/** Status pojedynczego meczu — odpowiada legendzie spod drabinki. */
export type MatchStatus =
  | 'finished'
  | 'live'
  /** Gra przerwana w toku (deszcz, kontuzja) — mecz czeka na wznowienie. */
  | 'suspended'
  | 'scheduled'
  | 'walkover'
  | 'retired';

/** Uczestnik meczu. */
export interface BracketPlayer {
  id: string;
  /** Nazwa wyswietlana, np. „M. Kowalski” albo para deblowa. */
  name: string;
  /** Numer rozstawienia. */
  seed?: number;
  /** Dwuliterowy kod kraju (ISO 3166-1 alpha-2), np. „PL”. */
  countryCode?: string;
  avatarUrl?: string;
}

/** Miejsce w drabince — zawodnik, wolny los albo jeszcze nierozstrzygniete. */
export type BracketSlot =
  | { kind: 'player'; player: BracketPlayer }
  | { kind: 'bye' }
  | { kind: 'pending'; label?: string };

export interface BracketMatch {
  id: string;
  home: BracketSlot;
  away: BracketSlot;
  /** Wyniki setow gospodarza, np. `[6, 4, 7]`. */
  homeScores?: readonly number[];
  awayScores?: readonly number[];
  /** Punkty tie-breaka w indeksie gornym, indeksowane numerem seta. */
  homeTiebreaks?: Readonly<Record<number, number>>;
  awayTiebreaks?: Readonly<Record<number, number>>;
  winner?: 'home' | 'away';
  status: MatchStatus;
  /** Etykieta terminu dla meczu jeszcze nierozegranego, np. „Dzisiaj, 12:00”. */
  scheduleLabel?: string;
  courtLabel?: string;
}

export interface BracketRound {
  id: string;
  /** Nazwa rundy, np. „1/8 finału”. */
  name: string;
  /** Data rundy pod nazwa, np. „24 maja”. */
  dateLabel?: string;
  matches: readonly BracketMatch[];
}

/** Zwraca zawodnika ze slotu albo `null`, gdy miejsce nie jest jeszcze obsadzone. */
export function playerOf(slot: BracketSlot): BracketPlayer | null {
  return slot.kind === 'player' ? slot.player : null;
}

/**
 * Czy mecz ma juz rozstrzygniecie i zwyciezce, ktory idzie dalej.
 *
 * Walkower liczy sie na rowni z rozegranym meczem — awansuje z niego zywy
 * zawodnik, wiec sciezka w drabince musi sie za nim podswietlic. Sprawdzanie
 * samego `status === 'finished'` gubilo wlasnie te galaz.
 */
export function matchAdvances(match: BracketMatch): boolean {
  return (
    (match.status === 'finished' || match.status === 'walkover') && match.winner !== undefined
  );
}

/**
 * Czy w meczu gra para deblowa/mixtowa (nazwa ze separatorem „ / ").
 * Karta meczu układa wtedy nazwiska jedno pod drugim i potrzebuje
 * wyższego wiersza w układzie drabinki.
 */
export function matchHasPair(match: BracketMatch): boolean {
  return [match.home, match.away].some(
    (slot) => slot.kind === 'player' && slot.player.name.includes(' / '),
  );
}

/**
 * Zwraca tekst do wyswietlenia w miejscu zawodnika — nazwisko, „Wolny los”
 * albo opis oczekiwania („Zwycięzca K1”).
 */
export function slotLabel(slot: BracketSlot, fallback = '—'): string {
  switch (slot.kind) {
    case 'player':
      return slot.player.name;
    case 'bye':
      return 'Wolny los';
    case 'pending':
      return slot.label ?? fallback;
  }
}
